/**
 * This is the report generation middleware.
 * APIs are called respectively if the action asks for a XLS
 * or a PDF report. Actions are dispatched on success case
 * to display the pdf report / save the xls file.
 * @param store
 * @returns {function(*): function(...[*]=)}
 */
import ReportGeneratorActions, {
    generateExcelReportSuccess,
    generatePDFReportSuccess, generateCSVReportSuccess
} from "../../report-generation/veritas-report-gen/ReportGeneratorActions";
import API from "../../common/utils/API";
import INPUT_FIELDS from "../../common/resources/inputfields.json"
import {displayErrorMessage} from "../../common/marvel-reducers/formMessageActions";

const NO_COUNTRIES_SOURCES_FOUND_ERROR = "No countries or sources are associated with the selected tenants.";
const REPORT_GENERATION_ERROR = "An error occurred while generating the report. Please try again.";

// NOTE:
// With DEV-1297, we have switched from using GET as request type for report generation
// to POST. This is a major change and decision was taken as such because of the large
// character number in the URL due to our guid length and added params of ISO Codes.
// This can be re-evaluated at some point, but the decision has been validated with the team.

const reportGenerationMiddleware = (store) => (next) => (action) => {

    switch (action.type) {

        case ReportGeneratorActions.Reports.CSV_REPORT:{
            next(action);

            const { recordIds, authToken } = action.payload;
            // this change is to accommodate a large number of guids that get passed along for
            // report generation. The api has been moved from a get to a post and therefore
            // sending the ids in the form of an object.
            const recordIdsObject = { recordIds }
            API.generateTestcacheCSVReport(recordIdsObject, authToken.idToken)
                .then(res => {
                    store.dispatch(generateCSVReportSuccess(res.data, "Testcache CSV Report.csv"))
                }).catch(error => {
                    console.error(error);
                });
            break;
        }

        case ReportGeneratorActions.Reports.KYB_CSV_REPORT:{
            next(action);

            const { recordIds, authToken } = action.payload;

            if (!authToken?.idToken) {
                console.error('KYB TestCache CSV Report: Missing auth token');
                return;
            }

            const recordIdsObject = { recordIds }
            API.generateKYBTestcacheCSVReport(recordIdsObject, authToken.idToken)
                .then(res => {
                    store.dispatch(generateCSVReportSuccess(res.data, "KYB TestCache CSV Report.csv"))
                }).catch(error => {
                    console.error('KYB Testcache CSV Report generation failed:', {
                        status: error.response?.status,
                        message: error.message,
                    });
                });
            break;
        }

        case ReportGeneratorActions.Reports.EXCEL_REPORT:{
            next(action);

            const { reportType, selectedServices, countryCodes, authToken } = action.payload;
            const formattedPayload = {
                reportType, selectedServices, countryCodes,
            }
            const filename = reportType === "internal"
                ? "Restricted-Internal-IDV Source Report.xlsx"
                : "LSEG IDV Source Report.xlsx"
            API.getSourcesForKYCXLSReport(reportType, formattedPayload, authToken.idToken)
                .then(res => {
                    store.dispatch(generateExcelReportSuccess(res.data, filename))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case ReportGeneratorActions.Reports.KYB_EXCEL_REPORT:{
            next(action);

            const { reportType, selectedServices, countryCodes, authToken } = action.payload;
            const formattedPayload = {
                reportType, selectedServices, countryCodes,
            }
            const filename = reportType === "internal"
                ? "Restricted-Internal-BV Source Report.xlsx"
                : "LSEG BV Source Report.xlsx"
            API.getSourcesForKYBXLSReport(reportType, formattedPayload, authToken.idToken)
                .then(res => {
                    store.dispatch(generateExcelReportSuccess(res.data, filename))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case ReportGeneratorActions.Reports.EXCEL_REPORT_FOR_ONE_TENANT: {
            next(action);

            const { useCase, requestPayload, authToken } = action.payload;
            const filename = useCase === "internal"
                ? "Restricted-Internal-Sources Associated with "+ requestPayload["tenant_name"] + " Tenant.xlsx"
                : "LSEG Sources Associated with "+ requestPayload["tenant_name"] + " Tenant.xlsx"
            API.getSourcesForXLSOneTenant(useCase, requestPayload, authToken.idToken)
                .then(res => {
                    store.dispatch(generateExcelReportSuccess(res.data, filename))
                }).catch(error => {
                console.error(error);
            });

            break;
        }

        case ReportGeneratorActions.Reports.KYB_COUNTRY_COVERAGE_EXCEL_REPORT:{
            next(action);

            const {type, authToken } = action.payload;
            API.generateKYBTypeCountryCoverageReport(type, authToken.idToken)
                .then(res => {
                    const report_filename = type.toUpperCase() + " - KYB Country Coverage.xlsx"
                    store.dispatch(generateExcelReportSuccess(res.data, report_filename))
                }).catch(error => {
                    console.error(error);
            });
            break;
        }

        case ReportGeneratorActions.Reports.PDF_REPORT: {
            next(action);

            const { selectedServices, countryCodes, authToken } = action.payload;
            const formattedPayload = {
                selectedServices, countryCodes,
            }
            API.getSourcesForKYCPDFReport(formattedPayload, authToken.idToken)
                .then(res => {
                    store.dispatch(generatePDFReportSuccess(
                        res.data,
                        INPUT_FIELDS))
                }).catch(error => {
                console.error(error);
                throw error;
                });

            break;
        }

        case ReportGeneratorActions.Reports.PDF_REPORT_FOR_ONE_TENANT: {
            next(action);

            const { requestPayload, authToken } = action.payload;
            API.getSourcesForPDFOneTenant(requestPayload, authToken.idToken)
                .then(res => {
                    store.dispatch(generatePDFReportSuccess(
                        res.data,
                        INPUT_FIELDS))
                }).catch(error => {
                console.error(error);
                throw error;
            });

            break;
        }

        case ReportGeneratorActions.Reports.KYB_PDF_REPORT: {
            next(action);
            const { selectedServices, countryCodes, authToken } = action.payload;
            const formattedPayload = {
                selectedServices, countryCodes,
            }
            API.getSourcesForKYBPDFReport(formattedPayload, authToken.idToken)
                .then(res => {
                    store.dispatch(generatePDFReportSuccess(
                        res.data,
                        INPUT_FIELDS))
                }).catch(error => {
                console.error(error);
                throw error;
                });

            break;
        }

        case ReportGeneratorActions.Reports.ALL_TENANTS_EXCEL_REPORT:{
            next(action);

            const { serviceID, selectedCountry, serviceType, authToken } = action.payload;
            API.generateAssociatedTenantsWithOneServiceReport(serviceType, serviceID, selectedCountry, authToken.idToken)
                .then(res => {
                    store.dispatch(generateExcelReportSuccess(res.data, "All Tenants Report.xlsx"))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case ReportGeneratorActions.Reports.GET_COUNTRIES_SOURCES: {
            next(action);
            const { authToken, tenantIds, comparisonTenantId } = action.payload;
            API.getCountriesSourcesForTenants(tenantIds, comparisonTenantId, authToken.idToken)
                .then(res => {
                    store.dispatch(generateExcelReportSuccess(res.data, "Tenant Countries and Sources Report.xlsx"))
                    store.dispatch(displayErrorMessage([], false));
                }).catch(error => {
                console.error(error);
                const message = error?.response?.status === 404
                    ? NO_COUNTRIES_SOURCES_FOUND_ERROR
                    : REPORT_GENERATION_ERROR;
                store.dispatch(displayErrorMessage([message], true));
            });
            break;
        }

        case ReportGeneratorActions.Reports.GET_TENANTS_PRICING_DATA: {
            next(action);
            const { authToken, tenantIds } = action.payload;
            API.getPricingDataForTenants(tenantIds, authToken.idToken)
                .then(res => {
                    store.dispatch(generateExcelReportSuccess(res.data, "Tenant Pricing Data Report.xlsx"))
                }).catch(error => {
                console.error(error);
                const message = REPORT_GENERATION_ERROR;
                store.dispatch(displayErrorMessage([message], true));
            });
            break;
        }

        default: next(action);
    }
}

export default reportGenerationMiddleware;
